import React, {FC, useState} from 'react';
import {AnimatePresence, domAnimation, LazyMotion, m} from 'framer-motion';
import Image from 'next/image';

import StepAge from '../../steps/StepAge';
import StepDebt from '../../steps/StepDebt';
import StepEstatePlanning from '../../steps/StepEstatePlanning';
import StepFinancialDependents from '../../steps/StepFinancialDependents';
import StepIncome from '../../steps/StepIncome';
import StepInsurance from '../../steps/StepInsurance';
import StepInvestment from '../../steps/StepInvestment';
import StepMaritalStatus from '../../steps/StepMaritalStatus';
import StepMonthlyExpenses from '../../steps/StepMonthlyExpenses';
import StepName from '../../steps/StepName';
import StepRealEstate from '../../steps/StepRealEstate';
import StepRetirement from '../../steps/StepRetirement';
import StepUnlockResults from '../../steps/StepUnlockResults';
import {useFhs} from '../../store/FhsContext/hooks';
import type {FhsStepName} from '../../types';
import RightPanel from '../RightPanel';
import StepNavigation from '../StepNavigation';
import StepShell from '../StepShell';

import {FORM_EXIT, SUMMARY_ENTER} from './constants';

import styles from './styles.module.scss';

const STEP_COMPONENTS: Partial<Record<FhsStepName, FC>> = {
  name: StepName,
  age: StepAge,
  maritalStatus: StepMaritalStatus,
  financialDependents: StepFinancialDependents,
  retirement: StepRetirement,
  insurance: StepInsurance,
  estatePlanning: StepEstatePlanning,
  income: StepIncome,
  investment: StepInvestment,
  realEstate: StepRealEstate,
  debt: StepDebt,
  monthlyExpenses: StepMonthlyExpenses,
};

const Layout: FC = () => {
  const {stepName} = useFhs();
  const isSummary = stepName === 'summary';
  /*
   * Remembers the step to render behind the summary. `lastFormStep` is its own
   * guard — once it matches `stepName` the condition stops firing — so the
   * separate "previous step" state this used to compare against was a second
   * render per step change for nothing.
   */
  const [lastFormStep, setLastFormStep] = useState<FhsStepName>(stepName);

  if (stepName !== 'summary' && stepName !== lastFormStep) {
    setLastFormStep(stepName);
  }

  const CurrentStep = STEP_COMPONENTS[lastFormStep];

  return (
    <LazyMotion features={domAnimation} strict>
      <AnimatePresence mode="wait" initial={false}>
        {isSummary ? (
          <m.div
            key="summary"
            initial={{opacity: 0, y: 12}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 12}}
            transition={SUMMARY_ENTER}>
            <StepUnlockResults />
          </m.div>
        ) : (
          <m.div
            key="form"
            className={styles.fhs}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={FORM_EXIT}>
            <main className={styles.fhs__main}>
              <div className={styles.fhs__main_header}>
                <Image
                  src="/static/img/zoe-logo/Zoe_Logo_Blue.svg"
                  alt="Financial Health Score"
                  width={100}
                  height={40}
                />
              </div>
              <div className={styles.fhs__content}>
                <StepShell>{CurrentStep && <CurrentStep />}</StepShell>
              </div>
              <StepNavigation />
            </main>
            <aside className={styles.fhs__panel}>
              <RightPanel />
            </aside>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};

export default Layout;
